/**
 * MermaidRenderer — renders mermaid diagrams from codeBlock nodes.
 *
 * Scans the editor document for codeBlock nodes with language "mermaid"
 * and renders the SVG output in a fixed panel below the editor.
 * Users see both the source code and the rendered diagram.
 *
 * Non-intrusive: doesn't modify BlockNote's schema or rendering.
 * Uses mermaid.render() for client-side SVG generation with debouncing.
 */

import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import type { BlockNoteEditor } from '@blocknote/core';

// Initialize mermaid once
let mermaidInitialized = false;
function initMermaid(): void {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'strict',
    fontFamily: 'inherit',
  });
  mermaidInitialized = true;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MermaidDiagram {
  readonly id: string;
  readonly source: string;
}

interface MermaidRendererProps {
  readonly editor: BlockNoteEditor<any, any, any> | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders all mermaid diagrams found in the editor as SVG previews.
 * Place this component below the editor — it renders diagram previews
 * in a separate section.
 */
export function MermaidRenderer({ editor }: MermaidRendererProps): React.ReactElement | null {
  const [diagrams, setDiagrams] = useState<MermaidDiagram[]>([]);

  // Scan editor for mermaid code blocks
  useEffect(() => {
    if (!editor) return;
    initMermaid();

    function scanBlocks(): void {
      const blocks = editor!.document;
      const found: MermaidDiagram[] = [];

      for (const block of blocks) {
        if (
          (block as any).type === 'codeBlock' &&
          (block as any).props?.language === 'mermaid'
        ) {
          const content = (block as any).content;
          let source = '';
          if (Array.isArray(content)) {
            source = content.map((c: any) => c.text ?? '').join('');
          }
          if (source.trim()) {
            found.push({ id: (block as any).id, source });
          }
        }
      }

      setDiagrams(found);
    }

    scanBlocks();
    const unsubscribe = editor.onChange(scanBlocks);
    return unsubscribe;
  }, [editor]);

  if (diagrams.length === 0) return null;

  return (
    <div className="border-t border-border mt-6 pt-4 space-y-4">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
        Diagram Previews
      </h4>
      {diagrams.map((diagram) => (
        <MermaidPreview key={diagram.id} id={diagram.id} source={diagram.source} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual diagram preview
// ---------------------------------------------------------------------------

interface MermaidPreviewProps {
  readonly id: string;
  readonly source: string;
}

function MermaidPreview({ id, source }: MermaidPreviewProps): React.ReactElement {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const renderCountRef = useRef(0);

  useEffect(() => {
    if (!source.trim()) {
      setSvg('');
      setError(null);
      return;
    }

    renderCountRef.current++;
    const currentRender = renderCountRef.current;

    const timer = setTimeout(async () => {
      try {
        // mermaid.render requires a unique DOM id
        const renderId = `mermaid-preview-${id}-${currentRender}`;
        const { svg: rendered } = await mermaid.render(renderId, source.trim());
        if (currentRender === renderCountRef.current) {
          setSvg(rendered);
          setError(null);
        }
      } catch (err) {
        if (currentRender === renderCountRef.current) {
          setSvg('');
          setError(err instanceof Error ? err.message : 'Diagram syntax error');
        }
      }
    }, 500); // Debounce 500ms to avoid re-rendering on every keystroke

    return () => clearTimeout(timer);
  }, [id, source]);

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      {svg && (
        <div
          className="p-4 flex justify-center bg-background [&>svg]:max-w-full [&>svg]:h-auto"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
      {error && (
        <div className="p-3 text-xs text-destructive bg-destructive/5">
          <p className="font-medium">Diagram error:</p>
          <pre className="mt-1 font-mono whitespace-pre-wrap">{error}</pre>
        </div>
      )}
      {!svg && !error && (
        <div className="p-3 text-xs text-muted-foreground italic">Rendering diagram...</div>
      )}
    </div>
  );
}
